import React from 'react';
import { cn } from '../../lib/utils';
import { KpiTileSkeleton } from '../accessibility/SkeletonLoader';
import { RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * MobileExecutiveView - Mobile-optimized executive dashboard
 * Features:
 * - Priority-based widget stacking
 * - Swipe navigation between widgets
 * - Touch-optimized controls (44px minimum)
 * - Offline-capable with cached data
 * - Pull-to-refresh
 */

const MobileExecutiveView = ({
  kpis = [],
  onRefresh,
  isLoading = false,
  lastUpdate,
  className = '',
}) => {
  const [expandedKpi, setExpandedKpi] = React.useState(null);
  const [isPulling, setIsPulling] = React.useState(false);
  const containerRef = React.useRef(null);

  // Priority order: P0 widgets first (top 5)
  const prioritizedKpis = React.useMemo(() => {
    return [...kpis].sort((a, b) => {
      const priorityOrder = { p0: 0, p1: 1, p2: 2 };
      return (priorityOrder[a.priority] || 1) - (priorityOrder[b.priority] || 1);
    });
  }, [kpis]);

  // Pull-to-refresh handler
  const handleTouchStart = React.useCallback((e) => {
    if (containerRef.current?.scrollTop === 0) {
      setIsPulling(true);
    }
  }, []);

  const handleTouchEnd = React.useCallback(() => {
    if (isPulling) {
      setIsPulling(false);
      onRefresh?.();
    }
  }, [isPulling, onRefresh]);

  if (isLoading && kpis.length === 0) {
    return (
      <div className={cn('space-y-4', className)}>
        {[1, 2, 3, 4, 5].map((i) => (
          <KpiTileSkeleton key={i} />
        ))}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn('space-y-3', className)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull-to-refresh indicator */}
      {isPulling && (
        <div className="flex justify-center py-4">
          <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-center justify-between px-1">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Key Metrics
        </h2>
        <button
          onClick={onRefresh}
          disabled={isLoading}
          className={cn(
            'p-2 rounded-lg touch-target',
            'hover:bg-gray-100 dark:hover:bg-gray-800',
            'disabled:opacity-50'
          )}
          aria-label="Refresh metrics"
        >
          <RefreshCw 
            size={18} 
            className={cn(
              'text-gray-500',
              isLoading && 'animate-spin'
            )} 
          />
        </button>
      </div>
      
      {/* KPI Cards - Stacked for mobile */}
      {prioritizedKpis.map((kpi, index) => (
        <MobileKpiCard
          key={kpi.id || index}
          kpi={kpi}
          isExpanded={expandedKpi === (kpi.id || index)}
          onToggle={() => setExpandedKpi(
            expandedKpi === (kpi.id || index) ? null : (kpi.id || index)
          )}
          priority={index < 5 ? 'p0' : 'p1'}
        />
      ))}
      
      {/* Last update time */}
      <div className="text-center text-xs text-gray-500 py-2">
        Last updated: {lastUpdate ? new Date(lastUpdate).toLocaleTimeString() : 'Never'}
      </div>
    </div>
  );
};

// Mobile KPI Card Component
const MobileKpiCard = ({
  kpi,
  isExpanded,
  onToggle,
  priority,
}) => {
  const getDeltaColor = (delta) => {
    if (!delta) return 'text-gray-500';
    const value = parseFloat(delta);
    if (value > 0) return 'text-semantic-success';
    if (value < 0) return 'text-semantic-danger';
    return 'text-gray-500';
  };

  return (
    <div
      className={cn(
        'glass-card p-4 transition-all duration-200',
        priority === 'p0' && 'border-l-4 border-l-urga-electricBlue'
      )}
    >
      {/* Collapsed view - always visible */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between touch-target"
        aria-expanded={isExpanded}
      >
        <div className="flex-1 text-left">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {kpi.title}
          </p>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {kpi.value}
            </span>
            {kpi.delta && (
              <span className={cn('text-sm font-medium', getDeltaColor(kpi.delta))}>
                {parseFloat(kpi.delta) > 0 ? '+' : ''}{kpi.delta}
              </span>
            )}
          </div>
        </div>
        <div className={cn(
          'transition-transform duration-200',
          isExpanded && 'rotate-180'
        )}>
          <ChevronDown size={20} className="text-gray-400" />
        </div>
      </button>
      
      {/* Expanded details */}
      <div className={cn(
        'overflow-hidden transition-all duration-200',
        isExpanded ? 'max-h-48 mt-4 opacity-100' : 'max-h-0 opacity-0'
      )}>
        {kpi.subcopy && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {kpi.subcopy}
          </p>
        )}
        
        {/* Sparkline placeholder */}
        {kpi.sparklineData && (
          <div className="h-12 bg-gray-100 dark:bg-gray-800 rounded-lg mb-2">
            {/* Sparkline would render here */}
          </div>
        )}
        
        {/* Footer info */}
        {kpi.footer && (
          <p className="text-xs text-gray-500 dark:text-gray-500 pt-2 border-t border-gray-200 dark:border-gray-700">
            {kpi.footer}
          </p>
        )}
      </div>
    </div>
  );
};

export default MobileExecutiveView;
